<template>
  <div class="question-main">
    <ClientForm
      :rut="rut"
      :color="color"
      @questionssingleformauth="startQuestions"
      @goBack="$emit('goBack')"
      :hasGoBack="hasGoBack"
      :skipFirstScreen="skipFirstScreen"
      v-if="!transactionId && !error && !finish"
    ></ClientForm>
    <Question
      :key="`question-${questions[questionNumber].questionId}`"
      v-if="transactionId && !error"
      :color="color"
      :durationInMillis="getTimerInMillis"
      :transactionId="transactionId"
      :question="questions[questionNumber]"
      @next="nextQuestion"
      @leavequestionauth="
        (reason) => {
          leavequestionauth(reason);
        }
      "
    ></Question>
    <div v-if="finish">
      <Result
        v-if="!error"
        title="Tu identidad ha sido verificada con éxito"
        body="Ya puedes continuar al siguiente paso."
      >
        <img
          slot="image"
          src="https://static.dx.principal.cl/fulfillment/assets/auth/kba/success_image.svg"
          alt="success_image"
        />
      </Result>
      <Result v-if="error" :title="errorMessageBody" body="">
        <img
          slot="image"
          src="https://static.dx.principal.cl/fulfillment/assets/auth/kba/error_image.svg"
          alt="error_image"
        />
      </Result>
    </div>
  </div>
</template>
<script>
import ClientForm from "./QuestionAuthentication/ClientForm";
import Question from "./QuestionAuthentication/QuestionComponent";
import Result from "./QuestionAuthentication/ResultComponent";
import { createLogger } from "@/utils/logger";
import { errorSources, errorMessageObtainer } from "@/utils/error-messages";
import { FFResultEvent } from "fulfillment-commons-js";

const logger = createLogger("QuestionAuthentication");
const AUTH_STEP = "equifaxKBAQuiz";

export default {
  name: "QuestionAuthentication",
  components: {
    ClientForm,
    Question,
    Result,
  },
  props: {
    company: { type: String, default: "principal" },
    rut: { type: String, required: true },
    questionService: { type: Object },
    color: { type: String, default: "#0091DA" },
    hasGoBack: { type: Boolean, default: false },
    traceClient: { type: Object, required: true },
    skipFirstScreen: { type: Boolean, default: false },
    customErrors: { type: Object },
    provider: { type: String, default: "EQUIFAX" },
    timeLimitMode: { type: String, default: "PER_ANSWER" },
    successCallback: { type: Function },
    failureCallback: { type: Function },
    portalId: { type: String },
    channelKey: { type: String },
  },
  data() {
    return {
      questionNumber: 0,
      error: false,
      finish: false,
      transactionId: null,
      durationInMillis: 10000,
      totalQuizDuration: 0,
      questions: [],
      errorMessageBody: "Ups, hubo un error de conexión, inténtalo nuevamente.",
    };
  },
  computed: {
    getTimerInMillis() {
      var timerInMillis = this.durationInMillis;
      if (this.timeLimitMode === "PER_QUIZ") {
        if (this.totalQuizDuration < timerInMillis) {
          timerInMillis -= this.totalQuizDuration;
        } else {
          timerInMillis = 0;
        }
      }
      return timerInMillis;
    },
  },
  methods: {
    successResponse(emitId, data) {
      if (typeof this.successCallback === "function") this.successCallback(data);
      this.$emit(emitId, data);
    },
    failureResponse(emitId, data) {
      if (typeof this.failureCallback === "function") this.failureCallback(data);
      this.$emit(emitId, data);
    },
    leavequestionauth(resp) {
      if (resp.reason === "canceled") {
        this.error = true;
        this.finish = true;
        this.transactionId = null;
        this.traceClient.error(`${AUTH_STEP}.cancelOperation`, "Operation Canceled", `{error: ${resp.reason}}`);
        const errorsResponse = {
          reason: "REQUEST_CANCELED",
          message: resp.reason + " userId:" + this.rut,
        };
        this.errorMessageBody = "Has cancelado la operación de autenticación.";
        this.refuse("canceled operation");
        this.failureResponse(
          "challengeAuth",
          FFResultEvent.failure(errorsResponse, this.traceClient.traceContext.traceId)
        );
      } else if (resp.reason === "timeout") {
        this.refuse("timeout");
        this.getResultAndShow(false);
      } else {
        this.refuse("unhandled error");
        this.getResultAndShow(false);
      }
    },
    async startQuestions(values) {
      this.traceClient.info(`${AUTH_STEP}.startQuestions`, "Creating tx", `{clientId: ${this.rut}}`);
      try {
        const data = await this.questionService.getQuestions(
          {
            ...values,
            company: this.company,
            provider: this.provider,
            timeLimitMode: this.timeLimitMode,
            portalId: this.portalId,
          },
          this.channelKey
        );
        if (data.success) {
          this.transactionId = data.transactionId;
          this.questions = data.questions;
          this.durationInMillis = data.answerTimeInMillis;
          this.totalQuizDuration = 0;
          this.questionNumber = 0;
          this.traceClient.info(
            `${AUTH_STEP}.startQuestions`,
            "Transaction created",
            `{rut: ${this.rut}, questions: ${this.questions.length}}`
          );
        } else {
          this.error = true;
          this.finish = true;
          this.traceClient.error(
            `${AUTH_STEP}.startQuestions`,
            "Couldn't create transaction",
            `{data: ${JSON.stringify(data)}}`
          );
          this.showCustomMessage(this.company, data.error.source, data.error.code);
          const errorsResponse = {
            reason: data.error.code,
            message: `Failure getting questions. ${data.error.detail}`,
          };
          this.failureResponse(
            "challengeAuth",
            FFResultEvent.failure(errorsResponse, this.traceClient.traceContext.traceId)
          );
        }
      } catch (e) {
        this.error = true;
        this.finish = true;
        logger.error(e);
        this.traceClient.error(
          `${AUTH_STEP}.startQuestions`,
          "Unhandled error",
          `{error: ${JSON.stringify(e.response)}}`
        );
        if (e.response) {
          const reasonError = e.response.code
            ? e.response.data.code
            : e.response.data.reason
            ? e.response.data.reason
            : e.status;
          this.showCustomMessage(this.company, "FULFILLMENT_AUTH_API", reasonError);
          const errorsResponse = {
            reason: reasonError,
            message: e.response.data.message,
          };
          this.failureResponse(
            "challengeAuth",
            FFResultEvent.failure(errorsResponse, this.traceClient.traceContext.traceId)
          );
        } else {
          const errorsResponse = {
            reason: e.name,
            message: e.message,
          };
          this.failureResponse(
            "challengeAuth",
            FFResultEvent.failure(errorsResponse, this.traceClient.traceContext.traceId)
          );
        }
      }
    },
    async nextQuestion(answer) {
      this.traceClient.info(
        `${AUTH_STEP}.nextQuestion`,
        `Setting answer for question ${this.questionNumber + 1}`,
        `{rut: ${this.rut}, questionNumber: ${this.questionNumber + 1}/${this.questions.length}}`
      );
      try {
        const data = await this.questionService.setAnswer(this.transactionId, answer, this.company);
        if (data.result !== "APPROVED") {
          this.getResultAndShow(true);
        } else {
          clearTimeout(this.timeout);
          this.totalQuizDuration += answer.durationInMillis;
          if (this.questionNumber < this.questions.length - 1) {
            this.questionNumber++;
          } else {
            this.submitQuizAndShow();
          }
        }
      } catch (err) {
        logger.error(err.response);
        this.traceClient.info(`${AUTH_STEP}.nextQuestion`, "Unhandled error", `{error: ${err.response}}`);
        if (!err.response || [500, 503, 408].indexOf(err.response.status) !== -1) {
          // add whatever error status code you want to try to send request again
          this.timeout = setTimeout(
            () =>
              this.nextQuestion({
                ...answer,
                durationInMillis: answer.durationInMillis + 1000,
              }),
            1000
          );
        } else {
          this.refuse("unhandled error");
          this.getResultAndShow(false);
        }
      }
    },
    refuse(error) {
      this.traceClient.info(`${AUTH_STEP}.refuse`, `Refusing quiz due to ${error}`, `{rut: ${this.rut}}`);
      return this.questionService.refuse(this.transactionId, this.company);
    },
    showCustomMessage(company, source, errorCode) {
      if (this.customErrors) {
        if (!!this.customErrors[errorCode] && !!this.customErrors[errorCode].message) {
          this.errorMessageBody = this.customErrors[errorCode].message;
        } else {
          this.errorMessageBody = errorMessageObtainer(company, source, errorCode);
        }
      } else {
        this.errorMessageBody = errorMessageObtainer(company, source, errorCode);
      }
    },
    async getResultAndShow(finish = false) {
      clearTimeout(this.timeout);
      try {
        this.traceClient.info(
          `${AUTH_STEP}.getResultAndShow`,
          "Getting result for completed quiz",
          `{rut: ${this.rut}}`
        );
        const data = await this.questionService.getResult(this.transactionId, this.company);
        this.showResult(data, finish);
      } catch (e) {
        this.error = true;
        this.finish = true;
        this.transactionId = null;
        logger.error(e);
        this.traceClient.error(`${AUTH_STEP}.getResultAndShow`, "Unhandled error", `{error: ${JSON.stringify(e)}}`);
        if (e.response) {
          const errorsResponse = {
            reason: e.response.status,
            message: e.response.data.error_description,
          };
          this.failureResponse(
            "challengeAuth",
            FFResultEvent.failure(errorsResponse, this.traceClient.traceContext.traceId)
          );
        } else {
          this.failureResponse(
            "challengeAuth",
            FFResultEvent.failure(
              { reason: "UNHANDLED_ERROR", message: JSON.stringify(e) },
              this.traceClient.traceContext.traceId
            )
          );
        }
      }
    },
    async submitQuizAndShow() {
      clearTimeout(this.timeout);
      try {
        this.traceClient.info(`${AUTH_STEP}.submitQuizAndShow`, "Submitting completed quiz", `{rut: ${this.rut}}`);
        const data = await this.questionService.updateQuiz(
          this.transactionId,
          {
            closed: true,
            answers: [],
            durationInMillis: this.totalQuizDuration,
            portalId: this.portalId,
          },
          this.company
        );
        this.showResult(data, true);
      } catch (e) {
        this.error = true;
        this.finish = true;
        this.transactionId = null;
        logger.error(e);
        this.traceClient.error(`${AUTH_STEP}.submitQuizAndShow`, "Unhandled error", `{error: ${JSON.stringify(e)}}`);
        if (e.response) {
          const errorsResponse = {
            reason: e.response.status,
            message: e.response.data.error_description,
          };
          this.failureResponse(
            "challengeAuth",
            FFResultEvent.failure(errorsResponse, this.traceClient.traceContext.traceId)
          );
        } else {
          this.failureResponse(
            "challengeAuth",
            FFResultEvent.failure(
              { reason: "UNHANDLED_ERROR", message: JSON.stringify(e) },
              this.traceClient.traceContext.traceId
            )
          );
        }
      }
    },
    async showResult(data, finish = false) {
      this.error = !finish;
      this.finish = true;
      this.transactionId = null;

      if (data.status === "APPROVED" && !this.error) {
        this.traceClient.success(`${AUTH_STEP}.showResult`, "Approved completed quiz", `{rut: ${this.rut}}`);
        this.successResponse("challengeAuth", FFResultEvent.success(data, this.traceClient.traceContext.traceId));
      } else if (data.status === "REJECTED" && !this.error) {
        this.error = true;
        if (data.error) {
          this.showCustomMessage(data.company, data.error.source, data.error.code);
        } else {
          this.showCustomMessage(data.company, errorSources.FULFILLMENT, "QUIZ_REJECTED_BY_PROVIDER");
        }
        this.traceClient.error(
          `${AUTH_STEP}.showResult`,
          "Completed quiz failed",
          `{rut: ${this.rut}}, data: ${JSON.stringify(data)}`
        );
        const errorResponse = {
          reason: data.status,
          message: `Challenge ${data.status}, rut: ${this.rut}}, data: ${JSON.stringify(data)}`,
        };
        this.failureResponse(
          "challengeAuth",
          FFResultEvent.failure(errorResponse, this.traceClient.traceContext.traceId)
        );
      } else {
        this.error = true;
        this.traceClient.error(
          `${AUTH_STEP}.showResult`,
          "Completed quiz failed",
          `{rut: ${this.rut}, data: ${JSON.stringify(data)}}`
        );
        this.showCustomMessage(data.company, data.error.source, data.error.code);
        const errorsResponse = {
          reason: data.error.code,
          message: `Challenge ${data.status}`,
        };
        this.failureResponse(
          "challengeAuth",
          FFResultEvent.failure(errorsResponse, this.traceClient.traceContext.traceId)
        );
      }
    },
  },
};
</script>

<style scoped>
@font-face {
  font-family: "FS Elliot Pro";
  src: url(https://static.dx.principal.cl/fulfillment/assets/dx/elliot/FSElliotPro-200.eot?#iefix) format("embedded-opentype"),
    url(https://static.dx.principal.cl/fulfillment/assets/dx/elliot/FSElliotPro-200.woff) format("woff"),
    url(https://static.dx.principal.cl/fulfillment/assets/dx/elliot/FSElliotPro-200.ttf) format("truetype"),
    url(https://static.dx.principal.cl/fulfillment/assets/dx/elliot/FSElliotPro-200.svg#FSElliotPro) format("svg");
  font-weight: 200;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: "FS Elliot Pro";
  src: url(https://static.dx.principal.cl/fulfillment/assets/dx/elliot/FSElliotPro-400.eot?#iefix) format("embedded-opentype"),
    url(https://static.dx.principal.cl/fulfillment/assets/dx/elliot/FSElliotPro-400.woff) format("woff"),
    url(https://static.dx.principal.cl/fulfillment/assets/dx/elliot/FSElliotPro-400.ttf) format("truetype"),
    url(https://static.dx.principal.cl/fulfillment/assets/dx/elliot/FSElliotPro-400.svg#FSElliotPro) format("svg");
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: "FS Elliot Pro";
  src: url(https://static.dx.principal.cl/fulfillment/assets/dx/elliot/FSElliotPro-600.eot?#iefix) format("embedded-opentype"),
    url(https://static.dx.principal.cl/fulfillment/assets/dx/elliot/FSElliotPro-600.woff) format("woff"),
    url(https://static.dx.principal.cl/fulfillment/assets/dx/elliot/FSElliotPro-600.ttf) format("truetype"),
    url(https://static.dx.principal.cl/fulfillment/assets/dx/elliot/FSElliotPro-600.svg#FSElliotPro) format("svg");
  font-weight: 600;
  font-style: normal;
  font-display: swap;
}

* {
  font-family: "FS Elliot Pro", Helvetica, Arial, sans-serif;
  text-align: left;
}
</style>
